/**
 * 分支状态管理 Store
 *
 * 使用 zustand 管理分支状态，包括分支树、当前分支、分支切换等
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { branchApi } from '../services/branchApi';
import type {
  Branch,
  BranchTreeNode,
  BranchStats,
  BranchSwitchResult,
} from '../types/branch';

interface BranchState {
  branchTree: BranchTreeNode[];
  currentBranch: Branch | null;
  isMainLine: boolean;
  branches: Branch[];
  branchStats: Map<number, BranchStats>;
  loading: boolean;
  error: string | null;
  workspaceId: number;
  expandedBranchIds: Set<number>;
  selectedBranchId: number | null;

  fetchBranchTree: (workspaceId: number) => Promise<void>;
  fetchBranchList: (workspaceId: number) => Promise<void>;
  switchBranch: (workspaceId: number, branchId: number | null) => Promise<BranchSwitchResult>;
  setCurrentBranch: (branch: Branch | null) => void;

  fetchBranchStats: (branchId: number) => Promise<BranchStats>;
  getCachedStats: (branchId: number) => BranchStats | undefined;

  toggleBranchExpand: (branchId: number) => void;
  setExpandedBranchIds: (ids: Set<number>) => void;
  setSelectedBranchId: (branchId: number | null) => void;

  getBranchById: (branchId: number) => Branch | undefined;
  getBranchPath: (branchId: number) => Branch[];
  findBranchInTree: (branchId: number) => BranchTreeNode | null;

  reorderBranches: (workspaceId: number, branches: Array<{ id: number; sort_order: number }>) => Promise<void>;

  clearError: () => void;
  reset: () => void;
}

const initialState = {
  branchTree: [],
  currentBranch: null,
  isMainLine: true,
  branches: [],
  branchStats: new Map<number, BranchStats>(),
  loading: false,
  error: null,
  workspaceId: 0,
  expandedBranchIds: new Set<number>(),
  selectedBranchId: null as number | null,
};

const flattenTree = (nodes: BranchTreeNode[]): Branch[] => {
  const result: Branch[] = [];
  const traverse = (node: BranchTreeNode) => {
    result.push(node.branch);
    node.children.forEach(traverse);
  };
  nodes.forEach(traverse);
  return result;
};

const findNodeInTree = (nodes: BranchTreeNode[], branchId: number): BranchTreeNode | null => {
  for (const node of nodes) {
    if (node.branch.id === branchId) {
      return node;
    }
    const found = findNodeInTree(node.children, branchId);
    if (found) {
      return found;
    }
  }
  return null;
};

const getBranchPathFromTree = (nodes: BranchTreeNode[], branchId: number, path: Branch[] = []): Branch[] | null => {
  for (const node of nodes) {
    const newPath = [...path, node.branch];
    if (node.branch.id === branchId) {
      return newPath;
    }
    const found = getBranchPathFromTree(node.children, branchId, newPath);
    if (found) {
      return found;
    }
  }
  return null;
};

export const useBranchStore = create<BranchState>()(
  immer((set, get) => ({
    ...initialState,

    fetchBranchTree: async (workspaceId: number) => {
      set((state) => {
        state.loading = true;
        state.error = null;
        state.workspaceId = workspaceId;
      });

      try {
        const tree = await branchApi.getBranchTree(workspaceId);

        set((state) => {
          state.branchTree = tree;
          state.branches = flattenTree(tree);
          state.loading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : '获取分支树失败';
          state.loading = false;
        });
        throw error;
      }
    },

    fetchBranchList: async (workspaceId: number) => {
      set((state) => {
        state.loading = true;
        state.error = null;
        state.workspaceId = workspaceId;
      });

      try {
        const branches = await branchApi.listBranches(workspaceId);

        set((state) => {
          state.branches = branches;
          state.loading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : '获取分支列表失败';
          state.loading = false;
        });
        throw error;
      }
    },

    switchBranch: async (workspaceId: number, branchId: number | null) => {
      set((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        const result = await branchApi.switchBranch(workspaceId, branchId);

        set((state) => {
          state.currentBranch = result.current_branch;
          state.isMainLine = result.is_main_line;
          state.selectedBranchId = branchId;
          state.loading = false;
        });

        if (branchId) {
          localStorage.setItem(`currentBranch_${workspaceId}`, String(branchId));
        } else {
          localStorage.removeItem(`currentBranch_${workspaceId}`);
        }

        return result;
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : '切换分支失败';
          state.loading = false;
        });
        throw error;
      }
    },

    setCurrentBranch: (branch: Branch | null) => {
      set((state) => {
        state.currentBranch = branch;
        state.isMainLine = branch === null;
        state.selectedBranchId = branch?.id ?? null;
      });
    },

    fetchBranchStats: async (branchId: number) => {
      try {
        const stats = await branchApi.getBranchStats(branchId);

        set((state) => {
          state.branchStats.set(branchId, stats);
        });

        return stats;
      } catch (error) {
        console.error('获取分支统计信息失败:', error);
        throw error;
      }
    },

    getCachedStats: (branchId: number) => {
      return get().branchStats.get(branchId);
    },

    toggleBranchExpand: (branchId: number) => {
      set((state) => {
        if (state.expandedBranchIds.has(branchId)) {
          state.expandedBranchIds.delete(branchId);
        } else {
          state.expandedBranchIds.add(branchId);
        }
      });
    },

    setExpandedBranchIds: (ids: Set<number>) => {
      set((state) => {
        state.expandedBranchIds = ids;
      });
    },

    setSelectedBranchId: (branchId: number | null) => {
      set((state) => {
        state.selectedBranchId = branchId;
      });
    },

    getBranchById: (branchId: number) => {
      return get().branches.find((b) => b.id === branchId);
    },

    getBranchPath: (branchId: number) => {
      const path = getBranchPathFromTree(get().branchTree, branchId);
      return path || [];
    },

    findBranchInTree: (branchId: number) => {
      return findNodeInTree(get().branchTree, branchId);
    },

    reorderBranches: async (workspaceId: number, branches: Array<{ id: number; sort_order: number }>) => {
      set((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        await branchApi.reorderBranches(workspaceId, branches);

        set((state) => {
          const orderMap = new Map(branches.map(b => [b.id, b.sort_order]));
          const updateTreeOrder = (nodes: BranchTreeNode[]): BranchTreeNode[] => {
            return nodes.map(node => {
              const newOrder = orderMap.get(node.branch.id);
              if (newOrder !== undefined) {
                node.branch.sort_order = newOrder;
              }
              return {
                ...node,
                children: updateTreeOrder(node.children),
              };
            });
          };
          state.branchTree = updateTreeOrder(state.branchTree);
          state.branches = state.branches.map(b => {
            const newOrder = orderMap.get(b.id);
            return newOrder !== undefined ? { ...b, sort_order: newOrder } : b;
          });
          state.loading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : '更新排序失败';
          state.loading = false;
        });
        throw error;
      }
    },

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    reset: () => {
      set(() => ({ ...initialState }));
    },
  }))
);

export default useBranchStore;